import Cookies from "js-cookie";
import { store } from "@root/store";
import { getAuthCookieName } from "@utils/auth";
import { RESPONSE_TYPES } from "@constants/response-types";
import { USER_TYPES } from "@constants/auth";
import { HttpService } from "@services/http";
import {
  prepareErrorResponse,
  prepareResponseObject,
} from "@services/http/response";
import { AUTH_RESET } from "@store/action-types";
import { LocalStorageService } from "../local-storage";
import { SessionStorageService } from "../session-storage";
import { persistor } from "@root/App";

const localStorageService = new LocalStorageService();
const sessionStorageService = new SessionStorageService();

export interface ISignup {
  name: string;
  email: string;
  slug: string;
  phone: string;
  password: string;
}
export class AuthService extends HttpService {
  signOut = async (): Promise<any> => {
    try {
      // Get current role before clearing
      const currentRole = sessionStorageService.getCurrentRole();

      // Remove cookie
      Cookies.remove(getAuthCookieName(process.env.REACT_APP_ENV));

      // Remove token for current role from localStorage
      if (currentRole) {
        await localStorageService.removeToken(currentRole);
      }

      // Also remove old authToken for backward compatibility
      await localStorageService.remove("authToken");

      // Clear tab-specific sessionStorage
      sessionStorageService.clear();

      // Purge Redux persist
      await persistor.purge();

      // Note: store dispatch and page reload are now handled in the logout thunk
    } catch (error) {
      throw prepareErrorResponse(error);
    }
  };

  signIn = async (
    baseAuthUrl: string,
    data: Record<string, TObject>,
    isSocial: boolean
  ): Promise<TObject> => {
    try {
      const apiResponse = await this.post(
        isSocial
          ? `${baseAuthUrl}/api/google/auth/verify`
          : `${baseAuthUrl}/api/auth/admin/signin`,
        data
      );

      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  signInAdmin = async (
    baseAuthUrl: string,
    data: Record<string, TObject>,
    isSocial: boolean
  ): Promise<TObject> => {
    try {
      const apiResponse = await this.post(
        isSocial
          ? `${baseAuthUrl}/api/google/auth/verify`
          : `${baseAuthUrl}/api/auth/signin`,
        {
          ...data,
          slug: data.slug ?? "",
        }
      );

      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  signUp = async (baseUrl: string, data: ISignup): Promise<TObject> => {
    try {
      const apiResponse = await this.post(
        `${baseUrl}/api/journalist-approval-request`,
        data
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  fullSignUp = async (
    baseUrl: string,
    data: ISignup,
    token: string
  ): Promise<TObject> => {
    try {
      const apiResponse = await this.patch(
        `${baseUrl}/api/user/profile/${token}`,
        data
      );

      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  resetPass = async (baseUrl: string, data: ISignup): Promise<TObject> => {
    try {
      const apiResponse = await this.post(
        `${baseUrl}/api/organization-membership/reset-password-with-otp`,
        data
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  verifyResetPass = async (
    baseUrl: string,
    token: string
  ): Promise<TObject> => {
    try {
      const apiResponse = await this.get(
        `${baseUrl}/api/user/reset-password/verification/${token}`
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  setNewPassword = async (
    baseUrl: string,
    data: {
      email: string;
      newPassword: string;
      confirmPassword: string;
      resetToken: string;
    }
  ): Promise<TObject> => {
    try {
      const apiResponse = await this.post(
        `${baseUrl}/api/organization-membership/set-new-password-after-otp`,
        data
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  deleteAccount = async (baseAuthUrl: string): Promise<TObject> => {
    try {
      const apiResponse = await this.delete(`${baseAuthUrl}/api/user`);

      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  changePasswordAccount = async (
    baseAuthUrl: string,
    payload: any
  ): Promise<TObject> => {
    try {
      const apiResponse = await this.post(
        `${baseAuthUrl}/api/auth/change-password
    `,
        payload
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  resendEmail = async (baseUrl: string, payload: any): Promise<TObject> => {
    try {
      const apiResponse = await this.post(
        `${baseUrl}/api/journalist-approval-request/resend-otp`,
        payload
      );

      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  verifyResetPasswordOtp = async (
    baseUrl: string,
    payload: { email: string; otp: string }
  ): Promise<TObject> => {
    try {
      const apiResponse = await this.post(
        `${baseUrl}/api/organization-membership/verify-reset-password-otp`,
        payload
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  verifySignupEmailOtp = async (
    baseUrl: string,
    email: string,
    otp: string
  ): Promise<TObject> => {
    try {
      console.log("verifySignupEmailOtp - email:", email);
      const apiResponse = await this.post(
        `${baseUrl}/api/journalist-approval-request/verify-otp`,
        { email: email, otp: otp }
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  updateProfile = async (
    id: number,
    payload: any,
    userRole?: number
  ): Promise<any> => {
    const baseAuthUrl = process.env.REACT_APP_BASE_URL;
    // Use different endpoint for super-admin
    const endpoint =
      userRole === USER_TYPES.SUPER_ADMIN
        ? "/api/user/update-profile"
        : `/api/organization-membership/${id}`;
    const baseUrl = `${baseAuthUrl}${endpoint}`;
    try {
      let options = undefined;
      if (payload instanceof FormData) {
        options = { headers: { "Content-Type": "multipart/form-data" } };
      }
      const apiResponse = await this.patch(baseUrl, payload, options);
      return apiResponse;
    } catch (error) {
      throw error;
    }
  };

  verifyOtp = async (payload: any): Promise<any> => {
    const baseAuthUrl = process.env.REACT_APP_BASE_URL;
    const baseUrl = `${baseAuthUrl}/api/organization-membership/verify-otp`;
    try {
      const apiResponse = await this.post(baseUrl, payload);
      return apiResponse;
    } catch (error) {
      throw error;
    }
  };

  sendOtp = async (payload: any): Promise<any> => {
    const baseAuthUrl = process.env.REACT_APP_BASE_URL;
    const baseUrl = `${baseAuthUrl}/api/organization-membership/send-otp`;

    try {
      const apiResponse = await this.post(baseUrl, payload);
      return apiResponse;
    } catch (error) {
      throw error;
    }
  };

  setCookies = async (): Promise<TObject> => {
    try {
      const baseAuthUrl = process.env.REACT_APP_BASE_URL;
      const apiResponse = await this.get(`${baseAuthUrl}/api/auth/set-cookies`);
      return apiResponse;
    } catch (error) {
      throw error;
    }
  };
}
